// Shop order templates — shared by the office board and the field create forms.
// Each type has its own fields; common fields (title, job, assignee, priority,
// needed-by, notes, photos) live outside this list.
export const SHOP_ORDER_TYPES = [
  {
    key: 'deliver_materials', label: 'Deliver Materials', icon: '📦', color: '#1e3a5f',
    blurb: 'Pipe, boxes, etc. — from stock or a vendor',
    fields: [
      { key: 'items', label: 'Items (what & how many)', type: 'textarea' },
      { key: 'source', label: 'Source', type: 'select', options: ['Stock', 'Vendor'] },
      { key: 'vendor', label: 'Vendor / details', type: 'text' },
      { key: 'deliver_to', label: 'Deliver to', type: 'text' },
    ],
  },
  {
    key: 'request_truck', label: 'Request a Truck', icon: '🚚', color: '#b45309',
    blurb: 'Move equipment, haul material, crane, water — the shop picks the truck',
    custom: true, // rendered by RequestTruckFields, not the flat field list
    fields: [],
  },
  {
    key: 'produce', label: 'Produce / Fabricate', icon: '🏭', color: '#4c1d95',
    blurb: 'Precast bases, light poles, etc.',
    fields: [
      { key: 'make_what', label: 'What to make', type: 'text' },
      { key: 'qty', label: 'Quantity', type: 'text' },
      { key: 'specs', label: 'Specs / notes', type: 'textarea' },
    ],
  },
  {
    key: 'request_equipment', label: 'Reserve Equipment', icon: '🚜', color: '#065f46',
    blurb: 'Any truck, trailer or machine — for a person, a day and a time',
    custom: true, // rendered by ReserveAssetFields (Ryan, Oct 1 2026: "pick any asset… for a time and a person")
    fields: [],
  },
  {
    key: 'fix_broken', label: 'Fix Something Broken', icon: '🔧', color: '#b91c1c',
    blurb: 'Report and repair a broken item',
    fields: [
      { key: 'item', label: 'What is broken', type: 'text' },
      { key: 'problem', label: 'What is wrong', type: 'textarea' },
    ],
  },
];
export const SHOP_ORDER_META = Object.fromEntries(SHOP_ORDER_TYPES.map(t => [t.key, t]));

// Request-a-Truck: the requester says what they NEED; the shop picks the truck
// (Ryan, Oct 1 2026: "move Equipment and let the shop pick a truck").
export const TRUCK_NEEDS = [
  { key: 'move_equipment', label: 'Move equipment', icon: '🚜', hint: 'A machine or trailer goes from where it is to a job (or back to the yard)' },
  { key: 'haul', label: 'Haul material', icon: '⛰️', hint: 'Sand, base, AC in — or spoils out' },
  { key: 'crane', label: 'Crane lift', icon: '🏗', hint: 'Set a pole, a base, a transformer…' },
  { key: 'water', label: 'Water', icon: '💧', hint: 'Dust control, compaction, fill a tank' },
  { key: 'other', label: 'Something else', icon: '🚚', hint: 'Describe it and the shop will sort out the truck' },
];
export const TRUCK_NEED_META = Object.fromEntries(TRUCK_NEEDS.map(n => [n.key, n]));
// Truck types — what the SHOP picks on the order (older orders had the requester pick).
export const TRUCK_TYPES = ['Transport', 'End Dump', 'Trout (live bottom)', 'Hook Truck', 'Crane Truck', '5 Yarder', 'Water Truck'];
export const HAUL_TYPES = ['Sand', 'Base', 'AC', 'Off-haul'];
// Sand/Base should reference an approved submittal material; these are dump types.
export const DUMP_TRUCK_TYPES = ['End Dump', 'Trout (live bottom)'];
export const WATER_USES = ['Dust control', 'Compaction', 'Fill a tank', 'Other'];
// Older orders only have a truck type — read the need off it.
export function truckNeedOf(d) {
  if (!d) return '';
  if (d.need) return d.need;
  const tt = d.truck_type || '';
  if (tt === 'Transport') return 'move_equipment';
  if (DUMP_TRUCK_TYPES.includes(tt)) return 'haul';
  if (tt === 'Crane Truck') return 'crane';
  if (tt === 'Water Truck') return 'water';
  return tt ? 'other' : '';
}

// When: a date (the order's needed_by) plus details.needed_time — 'AM', 'PM' or 'HH:MM'.
export const TIME_SLOTS = [['AM', 'Morning'], ['PM', 'Afternoon']];
export function timeLabel(t) {
  if (!t) return '';
  if (t === 'AM') return 'morning';
  if (t === 'PM') return 'afternoon';
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!m) return t;
  const h = Number(m[1]); const ap = h >= 12 ? 'PM' : 'AM';
  return `${((h + 11) % 12) + 1}:${m[2]} ${ap}`;
}
export function whenLabel(dateStr, t, opts = {}) {
  let day = '';
  if (dateStr) {
    const d = new Date(String(dateStr).slice(0, 10) + 'T00:00:00');
    if (!isNaN(d)) day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...(opts.year ? { year: 'numeric' } : {}) });
  }
  const tl = timeLabel(t);
  return [day, tl].filter(Boolean).join(' · ');
}

// Auto titles — the board reads "Alex · Big back trailer · Thu Oct 2 · 9:00 AM" instead of
// whatever someone typed in a Title box. The server builds the same when a title is blank.
export function truckRequestTitle(d, ctx = {}) {
  const need = truckNeedOf(d);
  const job = ctx.jobLabel || d.to_label || '';
  if (need === 'move_equipment') {
    const eq = ctx.equipmentLabel || d.custom_equipment || d.equipment_label || 'equipment';
    return `Move ${eq}${job ? ' → ' + job : ''}`;
  }
  if (need === 'haul') {
    const bits = [d.qty ? `${d.qty} ${d.qty_unit || 'yards'}` : null, d.material || d.haul_type || 'material'].filter(Boolean).join(' ');
    return `Haul ${bits}${job ? ' — ' + job : ''}`;
  }
  if (need === 'crane') return `Crane lift${d.lift_what ? ': ' + String(d.lift_what).split(/\r?\n/)[0] : ''}${job ? ' — ' + job : ''}`;
  if (need === 'water') return `Water truck${d.water_for && d.water_for !== 'Other' ? ' — ' + d.water_for : ''}${job ? ' — ' + job : ''}`;
  const what = String(d.what || '').split(/\r?\n/).map(x => x.trim()).find(Boolean);
  return what ? (what.length > 70 ? what.slice(0, 67).trimEnd() + '…' : what) : 'Truck request';
}
export function reservationTitle(d, neededBy, ctx = {}) {
  const who = ctx.personName || d.for_name || '';
  const what = ctx.assetLabel || d.asset_label || d.custom_asset || 'equipment';
  return [who, what, whenLabel(neededBy, d.needed_time)].filter(Boolean).join(' · ');
}

export const SHOP_PRIORITIES = [
  { key: 'low', label: 'Low', color: '#6b7280', bg: '#f3f4f6' },
  { key: 'normal', label: 'Normal', color: '#1d4ed8', bg: '#eff6ff' },
  { key: 'high', label: 'High', color: '#b45309', bg: '#fffbeb' },
  { key: 'urgent', label: 'Urgent', color: '#b91c1c', bg: '#fef2f2' },
];
export const SHOP_PRIORITY_META = Object.fromEntries(SHOP_PRIORITIES.map(p => [p.key, p]));

// Board columns. Working/Complete are manual; Upcoming/Due come from needed_by.
export const SHOP_COLUMNS = [
  { key: 'upcoming', label: 'Upcoming', color: '#6b7280' },
  { key: 'due', label: 'Due', color: '#b45309' },
  { key: 'working', label: 'Working On', color: '#1d4ed8' },
  { key: 'complete', label: 'Complete', color: '#15803d' },
];

// Days until a date (0 = today, 1 = tomorrow, negative = overdue).
export function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr.length <= 10 ? dateStr + 'T00:00:00' : dateStr);
  if (isNaN(d)) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0); d.setHours(0, 0, 0, 0);
  return Math.round((d - now) / 86400000);
}

// Which column an order belongs in.
export function shopOrderColumn(o) {
  if (o.work_status === 'complete') return 'complete';
  if (o.work_status === 'working') return 'working';
  const d = daysUntil(o.needed_by);
  if (d != null && d <= 1) return 'due';
  return 'upcoming';
}

// Human due label for a card.
export function dueLabel(dateStr) {
  const d = daysUntil(dateStr);
  if (d == null) return null;
  if (d < 0) return `Overdue ${-d}d`;
  if (d === 0) return 'Due today';
  if (d === 1) return 'Due tomorrow';
  return `Due in ${d}d`;
}
